import type { InstagramShareFormValues } from './types';

import { useCallback, useEffect, useRef, useState } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';

import {
  prepareInstagramImageFromBlob,
  prepareInstagramImageFromUrl,
} from './prepareInstagramImage';

export type InstagramImageStatus =
  | 'idle'
  | 'processing'
  | 'adapted'
  | 'notCroppable';

type UseInstagramImageAdaptationParams = {
  form: UseFormReturn<InstagramShareFormValues>;
  uploadImage: (file: File) => Promise<string>;
};

export type InstagramImageAdaptation = {
  status: InstagramImageStatus;
  adaptFile: (file: File) => Promise<void>;
  reset: () => void;
};

export function useInstagramImageAdaptation({
  form,
  uploadImage,
}: UseInstagramImageAdaptationParams): InstagramImageAdaptation {
  const { control, setValue } = form;
  const currentImageUrl = useWatch({ control, name: 'imageUrls' })?.[0];

  const ownImageUrlsRef = useRef<Set<string>>(new Set());
  const processingUrlRef = useRef<string | null>(null);
  const uploadImageRef = useRef(uploadImage);

  // Keep the latest uploadImage without adding it to the effect's deps below,
  // which would re-trigger processing whenever the parent passes a new reference.
  useEffect(() => {
    uploadImageRef.current = uploadImage;
  });

  const [status, setStatus] = useState<InstagramImageStatus>('idle');

  useEffect(() => {
    if (
      !currentImageUrl ||
      ownImageUrlsRef.current.has(currentImageUrl) ||
      processingUrlRef.current === currentImageUrl
    ) {
      return;
    }

    processingUrlRef.current = currentImageUrl;
    setStatus('processing');

    prepareInstagramImageFromUrl(currentImageUrl)
      .then(async (file) => {
        if (!file) {
          setStatus('notCroppable');
          setValue('imageUrls', []);

          return;
        }

        const url = await uploadImageRef.current(file);

        ownImageUrlsRef.current.add(url);
        setValue('imageUrls', [url], { shouldValidate: true });
        setStatus('adapted');
      })
      .catch(() => {
        setStatus('notCroppable');
        setValue('imageUrls', []);
      })
      .finally(() => {
        processingUrlRef.current = null;
      });
  }, [currentImageUrl, setValue]);

  const adaptFile = useCallback(
    async (file: File): Promise<void> => {
      setStatus('processing');

      try {
        const prepared = await prepareInstagramImageFromBlob(file);

        if (!prepared) {
          setStatus('notCroppable');
          setValue('imageUrls', []);

          return;
        }

        const url = await uploadImageRef.current(prepared);

        ownImageUrlsRef.current.add(url);
        setValue('imageUrls', [url], { shouldValidate: true });
        setStatus('idle');
      } catch {
        setStatus('notCroppable');
        setValue('imageUrls', []);
      }
    },
    [setValue]
  );

  const reset = useCallback(() => {
    setStatus('idle');
  }, []);

  return { status, adaptFile, reset };
}
